import React from "react";
import { Box, Grid, Skeleton, Stack } from "@mui/material";

/** Skeleton bars: dark mode shells need muted rails and a dimmer wave, not default light-grey wave chips on white. */
function skeletonBarSx(theme, extra = {}) {
  const dark = theme.palette.mode === "dark";
  return {
    ...extra,
    ...(dark
      ? {
          bgcolor: "rgba(255, 255, 255, 0.13)",
          "&::after": {
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
          },
        }
      : {}),
  };
}

/**
 * Generic list/toolbar placeholder — height follows content only (no viewport fill).
 */
export function AdminPageSkeleton({ minHeight = 0 }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        px: { xs: 1, sm: 2 },
        py: 2,
        minHeight,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Skeleton
            variant="rounded"
            width={44}
            height={40}
            sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
          />
          <Skeleton
            variant="text"
            width={180}
            sx={(t) => skeletonBarSx(t, { fontSize: "1.5rem" })}
          />
          <Box sx={{ flex: 1, minWidth: 24 }} />
          <Skeleton
            variant="rounded"
            width={120}
            height={36}
            sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
          />
          <Skeleton
            variant="rounded"
            width={100}
            height={36}
            sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
          />
        </Stack>
        <Skeleton
          variant="rounded"
          height={88}
          sx={(t) => skeletonBarSx(t, { borderRadius: "12px" })}
        />
        <Stack spacing={1}>
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={48}
              sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
              animation="wave"
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Profile / account-style forms: avatar + title lines + field grid. Fits content width only.
 */
export function AdminFormBlockSkeleton({ fieldRows = 8 }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        py: 1,
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Skeleton
            variant="circular"
            width={56}
            height={56}
            animation="wave"
            sx={(t) => skeletonBarSx(t, {})}
          />
          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton
              variant="text"
              width="min(280px, 55%)"
              sx={(t) => skeletonBarSx(t, { fontSize: "1.5rem" })}
            />
            <Skeleton
              variant="text"
              width="min(200px, 45%)"
              sx={(t) => skeletonBarSx(t, { fontSize: "1rem" })}
            />
          </Stack>
        </Stack>
        <Grid container spacing={2}>
          {Array.from({ length: fieldRows }, (_, i) => (
            <Grid
              key={i}
              size={{
                xs: 12,
                sm: 6
              }}>
              <Skeleton
                variant="rounded"
                height={46}
                sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
                animation="wave"
              />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
}

/**
 * For JobberListCard / table card body while rows load.
 */
export function AdminTableCardSkeleton({ rows = 12 }) {
  return (
    <Box sx={{ px: 2, py: 2.5, width: "100%", boxSizing: "border-box" }}>
      <Stack spacing={1.25}>
        {Array.from({ length: rows }, (_, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <Skeleton
              variant="rounded"
              height={36}
              sx={(t) => skeletonBarSx(t, { flex: 2, borderRadius: "6px" })}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              width="12%"
              height={36}
              sx={(t) => skeletonBarSx(t, { borderRadius: "6px" })}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              width="10%"
              height={36}
              sx={(t) => skeletonBarSx(t, { borderRadius: "6px" })}
              animation="wave"
            />
            <Skeleton
              variant="rounded"
              width={72}
              height={36}
              sx={(t) => skeletonBarSx(t, { borderRadius: "6px" })}
              animation="wave"
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Mirrors admin customer / quote / invoice detail chrome: top actions, header card, tabs, two-column body.
 * Use with LoaderComponent `skeletonLayout="detail"` (or import directly).
 */
export function AdminDetailPageSkeleton() {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        px: { xs: 1, sm: 2, md: 2.5 },
        py: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Skeleton
            variant="rounded"
            width={50}
            height={40}
            sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
            animation="wave"
          />
          <Box sx={{ flex: 1, minWidth: 8 }} />
          <Skeleton
            variant="rounded"
            width={140}
            height={36}
            sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
            animation="wave"
          />
          <Skeleton
            variant="rounded"
            width={44}
            height={36}
            sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
            animation="wave"
          />
        </Stack>

        <Box
          sx={(theme) => ({
            border: `1px solid ${
              theme.palette.mode === "dark" ? theme.palette.divider : "#E5E7EB"
            }`,
            borderRadius: "12px",
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : "#fff",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 1px 3px rgba(0, 0, 0, 0.35)"
                : "var(--shadow-md)",
          })}
        >
          <Box
            sx={(theme) => ({
              p: 2.5,
              borderBottom: `1px solid ${
                theme.palette.mode === "dark" ? theme.palette.divider : "#E5E7EB"
              }`,
            })}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              flexWrap="wrap"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  animation="wave"
                  sx={(t) => skeletonBarSx(t, {})}
                />
                <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width="min(300px, 70%)"
                    sx={(t) => skeletonBarSx(t, { fontSize: "1.75rem" })}
                    animation="wave"
                  />
                  <Skeleton
                    variant="text"
                    width="min(220px, 55%)"
                    sx={(t) => skeletonBarSx(t, { fontSize: "0.95rem" })}
                    animation="wave"
                  />
                </Stack>
              </Stack>
              <Stack spacing={1} sx={{ minWidth: 140, flexShrink: 0 }}>
                <Skeleton
                  variant="rounded"
                  height={20}
                  width={180}
                  sx={(t) => skeletonBarSx(t, { borderRadius: "4px" })}
                  animation="wave"
                />
                <Skeleton
                  variant="rounded"
                  height={20}
                  width={200}
                  sx={(t) => skeletonBarSx(t, { borderRadius: "4px" })}
                  animation="wave"
                />
              </Stack>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            sx={(theme) => ({
              px: 2,
              py: 1.5,
              flexWrap: "wrap",
              gap: 0.75,
              ...(theme.palette.mode === "dark"
                ? {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: "rgba(0,0,0,0.12)",
                  }
                : {}),
            })}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={96}
                height={36}
                sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
                animation="wave"
              />
            ))}
          </Stack>

          <Box sx={{ p: 2.5, pt: 1.5 }}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 8
                }}>
                <Stack spacing={1.25}>
                  {Array.from({ length: 7 }, (_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={44}
                      sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
                      animation="wave"
                    />
                  ))}
                </Stack>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Stack spacing={1.25}>
                  <Skeleton
                    variant="rounded"
                    height={120}
                    sx={(t) => skeletonBarSx(t, { borderRadius: "10px" })}
                    animation="wave"
                  />
                  {Array.from({ length: 4 }, (_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={36}
                      sx={(t) => skeletonBarSx(t, { borderRadius: "8px" })}
                      animation="wave"
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
